import { DayNavigationProvider } from "components/strategyDashboard/heats/context/DayNavigationProvider";
import {
  ShiftNavigationProvider,
  useShiftNavigation,
} from "components/strategyDashboard/heats/context/ShiftNavigationProvider";
import dayjs from "dayjs";
import { useIsBehindFeatureFlag } from "hooks/useFeatureFlag";
import { ReactNode, useEffect, useMemo, useRef } from "react";
import { HeatsDataProvider, useHeatsData } from "./HeatsDataContext";
import { HeatsFilterProvider } from "./HeatsFilterContext";
import { HeatsRowFilterProvider } from "./HeatsRowFilterContext";
import { HeatsSelectionProvider } from "./HeatsSelectionContext";

const hasExistingUrlParams = () => {
  const params = new URLSearchParams(window.location.search);
  return params.has("date") || params.has("shift");
};

const LatestShiftNavigator = () => {
  const shiftNav = useShiftNavigation();
  const { data } = useHeatsData();
  const hasResolvedRef = useRef(false);
  const skipResolve = useMemo(hasExistingUrlParams, []);

  useEffect(() => {
    if (
      skipResolve ||
      hasResolvedRef.current ||
      !shiftNav ||
      !data?.latest_heat_timestamp
    )
      return;
    if (data.series.length > 0) return;

    hasResolvedRef.current = true;

    const latestTimestamp = dayjs(data.latest_heat_timestamp);
    if (!latestTimestamp.isValid() || latestTimestamp.isAfter(dayjs())) return;

    void shiftNav.goToTimestamp(latestTimestamp);
  }, [skipResolve, data, shiftNav]);

  return null;
};

interface HeatsTabProviderProps {
  children: ReactNode;
}

export const HeatsTabProviders = ({ children }: HeatsTabProviderProps) => {
  const isShiftMode = useIsBehindFeatureFlag("heats_view_per_shift");

  const NavigationProvider = isShiftMode
    ? ShiftNavigationProvider
    : DayNavigationProvider;

  return (
    <NavigationProvider>
      <HeatsDataProvider>
        {isShiftMode ? <LatestShiftNavigator /> : null}
        <HeatsSelectionProvider>
          <HeatsFilterProvider>
            <HeatsRowFilterProvider>{children}</HeatsRowFilterProvider>
          </HeatsFilterProvider>
        </HeatsSelectionProvider>
      </HeatsDataProvider>
    </NavigationProvider>
  );
};
